import { useState } from 'react'
import type {
  CasePriority,
  ClientProfile,
  CreateCaseInput,
  CreateClientProfileInput,
} from '../../types/case-management'

export interface NewCaseFormState {
  name: string
  setName: (value: string) => void
  scopeDescription: string
  setScopeDescription: (value: string) => void
  selectedClientId: string | undefined
  setSelectedClientId: (value: string | undefined) => void
  orgCaseReference: string
  setOrgCaseReference: (value: string) => void
  priority: CasePriority
  setPriority: (value: CasePriority) => void
  showClientDropdown: boolean
  setShowClientDropdown: (value: boolean) => void
  clientSearch: string
  setClientSearch: (value: string) => void
  showNewClientForm: boolean
  setShowNewClientForm: (value: boolean) => void
  newClientName: string
  setNewClientName: (value: string) => void
  newClientShortName: string
  setNewClientShortName: (value: string) => void
  isValid: boolean
  resetForm: () => void
}

interface UseNewCaseFormArgs {
  onSubmit?: (data: CreateCaseInput) => void
  onCreateClient?: (data: CreateClientProfileInput) => Promise<ClientProfile | null>
}

export function useNewCaseForm(): NewCaseFormState {
  const [name, setName] = useState('')
  const [scopeDescription, setScopeDescription] = useState('')
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined)
  const [orgCaseReference, setOrgCaseReference] = useState('')
  const [priority, setPriority] = useState<CasePriority>('medium')
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [clientSearch, setClientSearch] = useState('')
  const [showNewClientForm, setShowNewClientForm] = useState(false)
  const [newClientName, setNewClientName] = useState('')
  const [newClientShortName, setNewClientShortName] = useState('')

  const isValid = Boolean(name.trim() && scopeDescription.trim())

  const resetForm = (): void => {
    setName('')
    setScopeDescription('')
    setSelectedClientId(undefined)
    setOrgCaseReference('')
    setPriority('medium')
    setClientSearch('')
    setShowNewClientForm(false)
    setNewClientName('')
    setNewClientShortName('')
  }

  return {
    name,
    setName,
    scopeDescription,
    setScopeDescription,
    selectedClientId,
    setSelectedClientId,
    orgCaseReference,
    setOrgCaseReference,
    priority,
    setPriority,
    showClientDropdown,
    setShowClientDropdown,
    clientSearch,
    setClientSearch,
    showNewClientForm,
    setShowNewClientForm,
    newClientName,
    setNewClientName,
    newClientShortName,
    setNewClientShortName,
    isValid,
    resetForm,
  }
}

function buildCaseInput(form: NewCaseFormState): CreateCaseInput {
  const input: CreateCaseInput = {
    name: form.name.trim(),
    scopeDescription: form.scopeDescription.trim(),
    priority: form.priority,
  }
  if (form.selectedClientId) input.clientProfileId = form.selectedClientId
  if (form.orgCaseReference.trim()) input.orgCaseReference = form.orgCaseReference.trim()
  return input
}

function buildClientInput(form: NewCaseFormState): CreateClientProfileInput {
  const input: CreateClientProfileInput = { name: form.newClientName.trim() }
  if (form.newClientShortName.trim()) input.shortName = form.newClientShortName.trim()
  return input
}

export function useNewCaseHandlers(
  form: NewCaseFormState,
  args: UseNewCaseFormArgs
): {
  handleSubmit: (e: React.FormEvent) => void
  handleCreateClient: () => Promise<void>
} {
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!form.isValid) return
    args.onSubmit?.(buildCaseInput(form))
    form.resetForm()
  }

  const handleCreateClient = async (): Promise<void> => {
    if (!form.newClientName.trim() || !args.onCreateClient) return
    const newClient = await args.onCreateClient(buildClientInput(form))
    if (newClient) {
      form.setSelectedClientId(newClient.id)
      form.setShowNewClientForm(false)
      form.setNewClientName('')
      form.setNewClientShortName('')
      form.setShowClientDropdown(false)
    }
  }

  return { handleSubmit, handleCreateClient }
}
